/**
 * Catalog item drawer (Catalog idea 2): every field of the item (part number, description, brand,
 * category, unit, min stock level, lead time, preferred supplier) saved with PUT /catalog/{id}, plus
 * "Where it is used" (on hand, Sites BOQ lines, bookings, last movement) and a duplicate warning with
 * Merge. Opened from a Catalog row or from Stock › Edit in Catalog. With QuickBooks Desktop read (`qb`, from
 * useQbStock), a read-only "In QuickBooks" block shows the matched QuickBooks item.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CopyCheck, GitMerge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SidePanel } from '@/components/ui/side-panel';
import { formatDate } from '@/lib/format';
import { LinkButton, UpperLabel } from './InventoryUi';
import { QbItemFacts, qbItemOf } from './QuickBooksStock';
import { DEFAULT_MIN_LEVEL, formatQty, groupMovements, documentLabel, isValidPartNumber, onHandOf, PART_NUMBER_INVALID, partKey, plural, samePartNumbers, toQty } from './inventoryModel';

const FIELDS = ['part_number', 'description', 'brand', 'category', 'unit', 'min_stock_level', 'lead_time_days', 'preferred_supplier'];
const label = 'mb-1 block text-[12px] font-semibold text-ink-2';

const draftOf = (item) => ({
  part_number: item?.part_number || '',
  description: item?.description || '',
  brand: item?.brand || '',
  category: item?.category || '',
  unit: item?.unit || 'pcs',
  min_stock_level: toQty(item?.min_stock_level) > 0 ? String(item.min_stock_level) : '',
  lead_time_days: toQty(item?.lead_time_days) > 0 ? String(item.lead_time_days) : '',
  preferred_supplier: item?.preferred_supplier || ''
});

export default function CatalogItemDrawer({
  open,
  onOpenChange,
  item,
  stock = [],
  sitesBoq = [],
  reservations = [],
  movements = [],
  facets,
  duplicates = [],
  canEdit = true,
  canMerge = false,
  onSave,
  onMerge,
  onOpenSitesBoq,
  onDirtyChange,
  qb = null
}) {
  const [draft, setDraft] = useState(() => draftOf(item));
  useEffect(() => { if (open) setDraft(draftOf(item)); }, [item, open]);

  const original = useMemo(() => draftOf(item), [item]);
  const changed = FIELDS.filter((field) => String(draft[field] ?? '').trim() !== String(original[field] ?? '').trim());
  const set = (field) => (event) => setDraft((current) => ({ ...current, [field]: event.target.value }));
  const dirtyCount = open ? changed.length : 0;
  useEffect(() => { onDirtyChange?.(dirtyCount); }, [dirtyCount, onDirtyChange]);

  const partNumber = item?.part_number || '';
  // Another row with exactly this number shares its stock and history: the server refuses a rename
  // until the two are merged (update_catalog_item).
  const exactTwin = duplicates.some((other) => other.id !== item?.id && String(other.part_number || '').trim() === partNumber.trim());
  const usage = useMemo(() => {
    const key = partKey(partNumber);
    const stockRow = stock.find((row) => row.part_number === partNumber);
    const boqRow = sitesBoq.find((row) => row.part_number === partNumber);
    const remaining = (boqRow?.project_details || []).reduce((sum, detail) => sum + toQty(detail.remaining_qty), 0);
    const bookings = reservations.filter((reservation) => partKey(reservation.part_number) === key);
    const last = groupMovements(movements.filter((movement) => movement.part_number === partNumber))[0];
    return {
      onHand: stockRow ? onHandOf(stockRow) : 0,
      boqProjects: (boqRow?.project_details || []).length,
      remaining,
      bookings: bookings.reduce((sum, reservation) => sum + toQty(reservation.qty_reserved), 0),
      last
    };
  }, [movements, partNumber, reservations, sitesBoq, stock]);

  const save = async () => {
    const payload = {};
    changed.forEach((field) => {
      const value = String(draft[field] ?? '').trim();
      if (field === 'min_stock_level' || field === 'lead_time_days') {
        const number = value === '' ? 0 : Number(value);
        if (!Number.isInteger(number) || number < 0) throw new Error(`${field === 'min_stock_level' ? 'Min stock level' : 'Lead time'} must be a whole number, 0 or more`);
        payload[field] = number;
      } else {
        if (field === 'part_number' && !value) throw new Error('Part number cannot be empty');
        // BUG-003: the server's rule and its exact words (routes/inventory.py PART_NUMBER_INVALID), before a 400
        if (field === 'part_number' && !isValidPartNumber(value)) throw new Error(PART_NUMBER_INVALID);
        if (field === 'unit' && !value) throw new Error('Unit cannot be empty');
        payload[field] = value;
      }
    });
    if (!Object.keys(payload).length) return;
    await onSave?.(item, payload);
  };

  const brandOptions = (facets?.brands || []).map((entry) => entry.label || entry.value).filter((value) => value && value !== '__none__');
  const categoryOptions = (facets?.categories || []).map((entry) => entry.label || entry.value).filter((value) => value && value !== '__none__');
  const supplierOptions = (facets?.suppliers || []).map((entry) => entry.label || entry.value).filter((value) => value && value !== '__none__');

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      modal={false}
      title={<span className="font-mono text-[16px]">{partNumber || 'Catalog item'}</span>}
      subtitle={item?.description || 'No description'}
      status={(
        <span className="flex flex-wrap gap-1">
          {item?.brand ? <span className="rounded-md border border-line bg-surface-2 px-1.5 text-[11px] font-bold text-ink-2">{item.brand}</span> : null}
          {item?.category ? <span className="rounded-md border border-line bg-surface-2 px-1.5 text-[11px] font-bold text-ink-2">{item.category}</span> : null}
        </span>
      )}
      dirty={changed.length}
      onSave={canEdit ? save : undefined}
      saveDisabled={!changed.length}
      testId="catalog-drawer"
    >
      <section className="rounded-xl border border-line bg-surface-2 px-4 py-3" aria-label="Where it is used" data-testid="catalog-drawer-usage">
        <div className="mb-2 flex items-center justify-between">
          <UpperLabel>Where it is used</UpperLabel>
          {usage.boqProjects ? <LinkButton className="text-[12.5px]" onClick={() => onOpenSitesBoq?.(partNumber)}>Open in Sites BOQ <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></LinkButton> : null}
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
          <div><dt className="text-ink-3">On hand</dt><dd className="font-semibold text-ink">{formatQty(usage.onHand)} pcs</dd></div>
          <div><dt className="text-ink-3">Sites BOQ</dt><dd className="font-semibold text-ink">{usage.boqProjects ? `${plural(usage.boqProjects, 'line')} · ${formatQty(usage.remaining)} pcs` : '—'}</dd></div>
          <div><dt className="text-ink-3">Bookings</dt><dd className="font-semibold text-ink">{formatQty(usage.bookings)}</dd></div>
          <div>
            <dt className="text-ink-3">Last movement</dt>
            <dd className="font-semibold text-ink">{usage.last ? `${documentLabel(usage.last)} · ${formatDate(usage.last.date, { year: 'auto' })}` : '—'}</dd>
          </div>
        </dl>
      </section>

      <QbItemFacts qbItem={qbItemOf(qb, partNumber)} here={usage.onHand} readAt={qb?.readAt} testId="catalog-drawer-qb" />

      {duplicates.length > 1 ? (
        <div className="flex items-start gap-3 rounded-xl border border-[color:var(--status-amber-bg)] bg-status-amber-bg px-4 py-3" data-testid="catalog-drawer-duplicate">
          <CopyCheck className="mt-0.5 h-4 w-4 shrink-0 text-status-amber-ink" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-[12.5px] text-ink">
            {samePartNumbers(duplicates) ? (
              <>
                <b className="block text-[13px] font-bold">Same part number appears {duplicates.length === 2 ? 'twice' : `${duplicates.length} times`}</b>
                Merging combines stock and moves the BOQ lines to one item.
              </>
            ) : (
              <>
                <b className="block text-[13px] font-bold">Part numbers match ignoring - and spaces</b>
                <span className="block font-mono text-[12px]">{duplicates.map((other) => other.part_number).join(' · ')}</span>
                <span className="block">Check the descriptions — merge only if they are the same item.</span>
              </>
            )}
          </div>
          {canMerge ? (
            <Button type="button" size="sm" variant="outline" className="shrink-0 bg-surface text-ink" onClick={() => onMerge?.(duplicates)} data-testid="catalog-drawer-merge">
              <GitMerge aria-hidden="true" /> Merge {duplicates.length} items
            </Button>
          ) : null}
        </div>
      ) : null}

      <fieldset disabled={!canEdit} className="grid gap-3">
        <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-3">
          <label className="block">
            <span className={label}>Part number</span>
            <Input
              value={draft.part_number}
              onChange={set('part_number')}
              disabled={exactTwin}
              title={exactTwin ? 'Merge the duplicates first, then rename' : undefined}
              className="font-mono"
              data-testid="catalog-drawer-part-number"
            />
          </label>
          <label className="block">
            <span className={label}>Unit</span>
            <Input value={draft.unit} onChange={set('unit')} list="catalog-unit-options" />
          </label>
        </div>
        {exactTwin ? (
          <p className="-mt-1 text-[11.5px] text-ink-3" data-testid="catalog-drawer-rename-blocked">Another item uses this exact number and shares its stock — merge them first, then rename.</p>
        ) : draft.part_number.trim() !== original.part_number.trim() ? (
          <p className="-mt-1 text-[11.5px] text-status-amber-ink" data-testid="catalog-drawer-rename-note">
            {isValidPartNumber(original.part_number)
              ? 'Stock, bookings, movement history and the project BOQ lines, site requests and delivery notes that use it move to the new number.'
              : 'Stock, bookings and movement history move to the new number. Project BOQ lines keep this placeholder.'}
          </p>
        ) : null}
        <label className="block">
          <span className={label}>Description</span>
          <Input value={draft.description} onChange={set('description')} data-testid="catalog-drawer-description" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={label}>Brand</span>
            <Input value={draft.brand} onChange={set('brand')} list="catalog-brand-options" placeholder="e.g. Schneider" />
          </label>
          <label className="block">
            <span className={label}>Category</span>
            <Input value={draft.category} onChange={set('category')} list="catalog-category-options" placeholder="e.g. Duct Sensors" />
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className={label}>Min stock level</span>
            <Input type="number" min="0" step="1" value={draft.min_stock_level} onChange={set('min_stock_level')} placeholder={`Default ${DEFAULT_MIN_LEVEL}`} data-testid="catalog-drawer-min" />
          </label>
          <label className="block">
            <span className={label}>Lead time (days)</span>
            <Input type="number" min="0" step="1" value={draft.lead_time_days} onChange={set('lead_time_days')} placeholder="Not set" data-testid="catalog-drawer-lead" />
          </label>
          <label className="block">
            <span className={label}>Preferred supplier</span>
            <Input value={draft.preferred_supplier} onChange={set('preferred_supplier')} list="catalog-supplier-options" placeholder="Choose…" data-testid="catalog-drawer-supplier" />
          </label>
        </div>
        <p className="-mt-1 text-[11.5px] text-ink-3">Min stock: low-stock alert below this · empty = default {DEFAULT_MIN_LEVEL}</p>
      </fieldset>
      {!canEdit ? <p className="text-[12px] text-ink-3">Your role can view this item but not change it.</p> : null}

      <datalist id="catalog-brand-options">{brandOptions.map((value) => <option key={value} value={value} />)}</datalist>
      <datalist id="catalog-category-options">{categoryOptions.map((value) => <option key={value} value={value} />)}</datalist>
      <datalist id="catalog-supplier-options">{supplierOptions.map((value) => <option key={value} value={value} />)}</datalist>
      <datalist id="catalog-unit-options">{['pcs', 'm', 'set', 'box', 'roll', 'kg'].map((value) => <option key={value} value={value} />)}</datalist>
    </SidePanel>
  );
}
